import React from "react";
import { Box, Chip } from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import {
  STATUS_FILTER_ALL,
  STATUS_FILTER_CANCELLED,
  STATUS_FILTER_COMPLETED,
  STATUS_FILTER_IN_PROGRESS,
  STATUS_FILTER_ORDER,
  STATUS_FILTER_PENDING,
  isStatusFilterActive,
  toggleStatusFilter,
} from "../../utils/taskStatusFilters";

// Each pill has its own bg + text/icon colour — some pairs are light-bg/dark-text
// (Pending, In Progress, Completed), others are dark-bg/white-text (All, Cancelled).
const FILTER_META = {
  [STATUS_FILTER_ALL]: {
    label: "All",
    icon: FilterListRoundedIcon,
    bgcolor: "#1e293b",
    textColor: "#ffffff",
  },
  [STATUS_FILTER_PENDING]: {
    label: "Pending",
    icon: AccessTimeRoundedIcon,
    bgcolor: "#f6ad37",
    textColor: "#7c4a03",
  },
  [STATUS_FILTER_IN_PROGRESS]: {
    label: "In Progress",
    icon: AutorenewRoundedIcon,
    bgcolor: "#93b8f2",
    textColor: "#1e3a8a",
  },
  [STATUS_FILTER_CANCELLED]: {
    label: "Cancelled",
    icon: HighlightOffRoundedIcon,
    bgcolor: "#f2726e",
    textColor: "#ffffff",
  },
  [STATUS_FILTER_COMPLETED]: {
    label: "Completed",
    icon: CheckCircleOutlineRoundedIcon,
    bgcolor: "#7fdcb0",
    textColor: "#065f46",
  },
};

const hexToRgb = (hex) => {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
};

const withAlpha = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};


const badgesWrapSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: { xs: 0.75, md: 0.6, lg: 0.65, xl: 0.7 },
  flexShrink: 0,
};

const getStatusBadgeSx = (meta, active) => (theme) => {
  const isDark = theme.palette.mode === "dark";
  const base = {
    fontWeight: active ? 700 : 600,
    fontSize: { xs: "0.82rem", sm: "0.88rem", md: "0.82rem", lg: "0.86rem", xl: "0.88rem" },
    height: { xs: 30, sm: 32, md: 28, lg: 30, xl: 32 },
    "& .MuiChip-label": {
      px: { xs: 1.25, md: 1.1, lg: 1.1, xl: 1.15 },
      py: { md: 0, lg: 0.1, xl: 0.15 },
      color: "inherit",
    },
    "& .MuiChip-icon": {
      color: "inherit",
      fontSize: "1.05rem",
      marginLeft: "10px",
    },
    borderRadius: "999px",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border-color", "color", "box-shadow"], {
      duration: theme.transitions.duration.shortest,
    }),
  };

  const accent = meta.bgcolor;
  const textColor = meta.textColor;

  // MUI's own clickable-Chip variant bakes in a translucent grey ":hover"
  // background at the SAME selector specificity as a plain "&:hover" sx
  // override, so which one wins is a coin flip — when MUI's wins, our
  // label text sits on a near-white overlay and effectively disappears.
  // Doubling the selector ("&&:hover") raises our specificity above MUI's
  // built-in rule so our colour always wins on hover.

  // Selected: full-strength colour, lifted with a soft shadow — clearly active.
  if (active) {
    return {
      ...base,
      bgcolor: accent,
      color: textColor,
      border: `1.5px solid ${accent}`,
      boxShadow: `0 2px 8px ${withAlpha(accent, 0.5)}`,
      opacity: 1,
      "&&:hover": {
        bgcolor: accent,
        filter: "brightness(1.04)",
      },
    };
  }

  // Unselected: same colour but dimmed so it recedes next to the selected pill.
  return {
    ...base,
    bgcolor: accent,
    color: textColor,
    border: `1.5px solid ${accent}`,
    boxShadow: "none",
    opacity: isDark ? 0.5 : 0.45,
    "&&:hover": {
      bgcolor: accent,
      opacity: isDark ? 0.72 : 0.68,
    },
  };
};

/** Maps a status filter key to its field on the `counts` object from the status-counts API. */
const COUNT_KEY_BY_FILTER = {
  [STATUS_FILTER_ALL]: "all",
  [STATUS_FILTER_PENDING]: "pending",
  [STATUS_FILTER_IN_PROGRESS]: "inProgress",
  [STATUS_FILTER_CANCELLED]: "cancelled",
  [STATUS_FILTER_COMPLETED]: "completed",
};

const TaskStatusFilterBadges = ({ value, onChange, counts }) => {
  const handleToggle = (key) => {
    onChange?.(toggleStatusFilter(value, key));
  };

  return (
    <Box sx={badgesWrapSx} role="group" aria-label="Filter by status">
      {STATUS_FILTER_ORDER.map((key) => {
        const meta = FILTER_META[key];
        const active = isStatusFilterActive(value, key);
        const Icon = meta.icon;
        const count = counts ? counts[COUNT_KEY_BY_FILTER[key]] ?? 0 : null;

        return (
          <Chip
            key={key}
            icon={<Icon />}
            label={count != null ? `${meta.label} (${count})` : meta.label}
            clickable
            onClick={() => handleToggle(key)}
            variant="filled"
            sx={getStatusBadgeSx(meta, active)}
          />
        );
      })}
    </Box>
  );
};

export default TaskStatusFilterBadges;
